// Chat list filters, like WhatsApp's: All, Unread, Favourites, Groups and
// lists you make yourself ("Work", "Family"…). Favourites and lists are kept
// per account on this device.
import { signal } from "@preact/signals";
import { useState } from "preact/hooks";
import * as S from "../state";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { toast } from "./Main";

export interface ChatList {
  id: string;
  name: string;
  chats: string[];
}
interface Store {
  favourites: string[];
  lists: ChatList[];
}

export type Filter = "all" | "unread" | "favourites" | "groups" | `list:${string}`;

let account = "";
const key = () => "xmm.lists." + account;
export const lists = signal<Store>({ favourites: [], lists: [] });

export function loadLists(jid: string) {
  account = jid;
  try {
    lists.value = { favourites: [], lists: [], ...(JSON.parse(localStorage.getItem(key()) ?? "{}") as Partial<Store>) };
  } catch {
    lists.value = { favourites: [], lists: [] };
  }
}
function save(s: Store) {
  lists.value = s;
  try {
    localStorage.setItem(key(), JSON.stringify(s));
  } catch {
    /* storage blocked: kept for this session */
  }
}

export const isFavourite = (jid: string) => lists.value.favourites.includes(jid);
export function toggleFavourite(jid: string) {
  const s = lists.value;
  const on = s.favourites.includes(jid);
  save({ ...s, favourites: on ? s.favourites.filter((j) => j !== jid) : [...s.favourites, jid] });
  toast(on ? "Removed from favourites" : "Added to favourites");
}
export function toggleInList(id: string, jid: string) {
  const s = lists.value;
  save({ ...s, lists: s.lists.map((l) => (l.id === id ? { ...l, chats: l.chats.includes(jid) ? l.chats.filter((j) => j !== jid) : [...l.chats, jid] } : l)) });
}

/** Does this chat belong in the filter? */
export function inFilter(f: Filter, jid: string, unread: number) {
  if (f === "unread") return unread > 0;
  if (f === "favourites") return lists.value.favourites.includes(jid);
  if (f === "groups") return S.isGroup(jid);
  if (f.startsWith("list:")) return !!lists.value.lists.find((l) => "list:" + l.id === f)?.chats.includes(jid);
  return true;
}

export const filterLabel = (f: Filter) =>
  f === "all" ? "All" : f === "unread" ? "Unread" : f === "favourites" ? "Favourites" : f === "groups" ? "Groups" : (lists.value.lists.find((l) => "list:" + l.id === f)?.name ?? "List");

/** The chips above the chat list. */
export function FilterChips({ filter, onChange }: { filter: Filter; onChange: (f: Filter) => void }) {
  const [editing, setEditing] = useState<ChatList | "new" | null>(null);
  const fixed: Filter[] = ["all", "unread", "favourites", "groups"];
  const current = filter.startsWith("list:") ? lists.value.lists.find((l) => "list:" + l.id === filter) : undefined;
  return (
    <>
      <div class="chips" role="tablist" aria-label="Chat filters">
        {[...fixed, ...lists.value.lists.map((l) => `list:${l.id}` as Filter)].map((f) => (
          <button key={f} role="tab" aria-selected={filter === f} class={"chip" + (filter === f ? " on" : "")} onClick={() => onChange(f)}>
            {filterLabel(f)}
          </button>
        ))}
        {current && (
          <button class="chip icon-chip" onClick={() => setEditing(current)} aria-label={`Edit list ${current.name}`} title="Edit list">
            <Icon name="edit" size={14} />
          </button>
        )}
        <button class="chip icon-chip" onClick={() => setEditing("new")} aria-label="New list" title="New list">
          <Icon name="plus" size={14} />
        </button>
      </div>
      {editing && (
        <ListEditor
          list={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(id) => onChange(`list:${id}`)}
          onDeleted={() => onChange("all")}
        />
      )}
    </>
  );
}

function ListEditor({ list, onClose, onSaved, onDeleted }: { list: ChatList | null; onClose: () => void; onSaved: (id: string) => void; onDeleted: () => void }) {
  const [name, setName] = useState(list?.name ?? "");
  const [picked, setPicked] = useState<string[]>(list?.chats ?? []);
  const [q, setQ] = useState("");
  const chats = S.chats.value.filter((c) => !S.isChannel(c.jid) && (!q || S.displayName(c.jid).toLowerCase().includes(q.toLowerCase())));
  function saveList() {
    const n = name.trim();
    if (!n) return;
    const s = lists.value;
    const id = list?.id ?? crypto.randomUUID().slice(0, 8);
    const next = { id, name: n.slice(0, 40), chats: picked };
    save({ ...s, lists: list ? s.lists.map((l) => (l.id === id ? next : l)) : [...s.lists, next] });
    onClose();
    onSaved(id);
  }
  function remove() {
    const s = lists.value;
    save({ ...s, lists: s.lists.filter((l) => l.id !== list!.id) });
    onClose();
    onDeleted();
  }
  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal list-editor" role="dialog" aria-label={list ? "Edit list" : "New list"} onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>{list ? "Edit list" : "New list"}</h2>
        </header>
        <div class="form-body">
          <input class="line-input" value={name} maxLength={40} placeholder="List name, e.g. Work" aria-label="List name" autoFocus onInput={(e) => setName(e.currentTarget.value)} />
          <input class="line-input" value={q} placeholder="Search chats" aria-label="Search chats to add" onInput={(e) => setQ(e.currentTarget.value)} />
          <ul class="pick-list">
            {chats.map((c) => (
              <li key={c.jid} class={picked.includes(c.jid) ? "picked" : ""} onClick={() => setPicked(picked.includes(c.jid) ? picked.filter((j) => j !== c.jid) : [...picked, c.jid])}>
                <Avatar url={S.isGroup(c.jid) ? S.groups.value.get(c.jid)?.avatar_url : S.profiles.value.get(c.jid)?.avatar_url} jid={c.jid} name={S.displayName(c.jid)} size={36} />
                <span class="grow">{S.displayName(c.jid)}</span>
                <span class={"poll-check multi" + (picked.includes(c.jid) ? " on" : "")}>{picked.includes(c.jid) && <Icon name="check" size={14} />}</span>
              </li>
            ))}
            {!chats.length && <li class="empty">No chats</li>}
          </ul>
        </div>
        <footer class="forward-footer">
          {list ? (
            <button class="link danger" onClick={remove}>
              Delete list
            </button>
          ) : (
            <span class="muted">{picked.length} chats</span>
          )}
          <button class="icon-btn send-fab" disabled={!name.trim()} onClick={saveList} aria-label="Save list">
            <Icon name="check" />
          </button>
        </footer>
      </div>
    </div>
  );
}
